import { createMarriottHotelLink } from "~/utils/createMarriottLink";

export default function useNavigationItems() {
  const { hotelsByContinentAndCity } = useHotels();
  const { locale, t } = useI18n();
  const localePath = useLocalePath();

  const { data: contentfulNavigation } = useFetch("/api/v2/navigation", {
    query: {
      locale,
    },
  });

  const hotelNavigationItems = computed(() => {
    return Object.values(hotelsByContinentAndCity.value).map(
      ({ slug: continentSlug, title: continentTitle, cities }) => ({
        slug: localePath({
          name: "hotels-continent",
          params: { continent: continentSlug },
        }),
        title: continentTitle,
        navigationItems: Object.values(cities).map(
          ({ slug: citySlug, title: cityTitle, hotels }) => {
            const singleHotel = hotels.length === 1 ? hotels[0] : null;

            return {
              slug: localePath({
                name: "hotels-continent-city",
                params: { continent: continentSlug, city: citySlug },
              }),
              externalLinkUrl:
                singleHotel && singleHotel.crsEnabled
                  ? createMarriottHotelLink(singleHotel.crsCode, locale.value)
                  : undefined,
              title: cityTitle,
              navigationItems: hotels.map((hotel) => {
                // For failsafe, ensure we try to split a string. Fallbacks for continent,
                // city, and hotelSlug will be undefined, which means the link cannot be generated.
                // However, this makes sure at least we prevent an error from being thrown without proper ErrorBoundary.
                const [continent, city, hotelSlug] = String(hotel.slug).split(
                  "/",
                );

                return {
                  slug: localePath({
                    name: "hotels-continent-city-hotelSlug",
                    params: { continent, city, hotelSlug },
                  }),
                  externalLinkUrl: hotel.crsEnabled
                    ? createMarriottHotelLink(hotel.crsCode, locale.value)
                    : undefined,
                  title: hotel.name,
                };
              }),
            };
          },
        ),
      }),
    );
  });

  const navBarItems = computed(() => {
    return {
      languages: contentfulNavigation.value?.languages,
      navigationItems: [
        {
          title: t("website.navigation.hotels.title"),
          navigationItems: hotelNavigationItems.value,
        },
        ...(contentfulNavigation.value?.navigationItems ?? []),
      ],
    };
  });

  return {
    navBarItems,
    hotelItems: hotelNavigationItems.value,
  };
}
